import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Phone, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, useApi, errMsg } from "@/pos/lib/api";
import { rm } from "@/pos/lib/format";
import { Page, PageHeader, Stat, Empty } from "@/pos/components/Common";
import Field from "@/pos/components/Field";

export function CustomerDialog({ open, onClose, onSaved, customer }) {
  const [f, setF] = useState({ name: "", phone: "", address: "" });
  const [prev, setPrev] = useState(null);
  if (open && prev !== (customer || "new")) {
    setPrev(customer || "new");
    setF(customer ? { name: customer.name, phone: customer.phone, address: customer.address } : { name: "", phone: "", address: "" });
  }
  const save = async () => {
    try {
      if (customer) await api.put(`/customers/${customer.id}`, f);
      else await api.post("/customers", f);
      toast.success("Pelanggan disimpan");
      setPrev(null);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setPrev(null); onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="customer-dialog">
        <DialogHeader><DialogTitle className="font-heading">{customer ? "Kemaskini Pelanggan" : "Pelanggan Baru"}</DialogTitle></DialogHeader>
        <Field label="Nama"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="customer-name-input" /></Field>
        <Field label="No. Telefon"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="customer-phone-input" /></Field>
        <Field label="Alamat"><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="customer-address-input" /></Field>
        <button onClick={save} disabled={!f.name} className="btn-primary h-11 w-full disabled:opacity-40" data-testid="customer-save-btn">Simpan</button>
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const { data, reload } = useApi("/customers");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const list = (data || []).filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  const totalDebt = (data || []).reduce((a, c) => a + Math.max(c.balance, 0), 0);
  return (
    <Page>
      <PageHeader title="Pelanggan & Hutang" subtitle="Buku hutang">
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="add-customer-btn"><Plus size={16} /> Pelanggan Baru</button>
      </PageHeader>
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Jumlah Hutang" value={rm(totalDebt)} tone="amber" testid="customers-total-debt" />
        <Stat label="Pelanggan" value={(data || []).length} testid="customers-count" />
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari pelanggan..." className="mb-4 h-11 max-w-sm bg-white" data-testid="customer-search-input" />
      <div className="card-surface divide-y divide-slate-100" data-testid="debtors-tab">
        {list.map((c) => (
          <Link key={c.id} to={`/pos/pelanggan/${c.id}`} data-testid={`customer-row-${c.id}`} className="flex items-center gap-4 p-4 transition-colors hover:bg-slate-50">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-red-50 font-heading font-bold text-red-700">{c.name[0]}</div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{c.name}</div>
              {c.phone && <div className="flex items-center gap-1 text-xs text-slate-500"><Phone size={12} />{c.phone}</div>}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Baki Hutang</div>
              <div className={`font-mono font-bold ${c.balance > 0 ? "text-amber-600" : "text-green-700"}`}>{rm(c.balance)}</div>
            </div>
            <ChevronRight size={18} className="text-slate-300" />
          </Link>
        ))}
        {list.length === 0 && <Empty text="Tiada pelanggan" />}
      </div>
      <CustomerDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); reload(); }} />
    </Page>
  );
}
